import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Upload,
  Settings,
  Users,
  Link,
  ListTodo,
  Sun,
  Sunset,
  Target,
  BarChart3,
  AlertTriangle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { CalendarView, TimeFormat } from '../types';
import type { CalendarEventInstance } from '@/lib/api';
import { formatDateLabel } from '../utils';
import { SearchBar } from './SearchBar';

interface CalendarToolbarProps {
  currentDate: Date;
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onNewEvent: () => void;
  onImport: () => void;
  onSettings: () => void;
  onFindTime?: () => void;
  onSchedulingLinks?: () => void;
  onToggleTasks?: () => void;
  showTaskPanel?: boolean;
  onDailyPlanning?: () => void;
  onEveningReview?: () => void;
  onFocusTime?: () => void;
  searchQuery?: string;
  onSearchQueryChange?: (q: string) => void;
  searchResults?: CalendarEventInstance[];
  searchLoading?: boolean;
  onSearchSelect?: (event: CalendarEventInstance) => void;
  onSearchDeleteEvents?: (eventIds: string[]) => Promise<void>;
  onSearchClear?: () => void;
  timeFormat?: TimeFormat;
  meetingWarning?: { count: number; max: number } | null;
}

export function CalendarToolbar({
  currentDate,
  view,
  onViewChange,
  onPrev,
  onNext,
  onToday,
  onNewEvent,
  onImport,
  onSettings,
  onFindTime,
  onSchedulingLinks,
  onToggleTasks,
  showTaskPanel,
  onDailyPlanning,
  onEveningReview,
  onFocusTime,
  searchQuery,
  onSearchQueryChange,
  searchResults,
  searchLoading,
  onSearchSelect,
  onSearchDeleteEvents,
  onSearchClear,
  timeFormat,
  meetingWarning,
}: CalendarToolbarProps) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-between border-b px-4 py-2 gap-2 flex-wrap">
      {/* Left: Navigation */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onToday}>
          Today
        </Button>
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onPrev}
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onNext}
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <h2 className="text-lg font-semibold whitespace-nowrap">
          {formatDateLabel(currentDate, view)}
        </h2>

        {meetingWarning && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-xs font-medium">
            <AlertTriangle className="h-3 w-3" />
            {meetingWarning.count}/{meetingWarning.max} meetings today
          </span>
        )}

        {onSearchQueryChange && onSearchSelect && onSearchClear && (
          <SearchBar
            query={searchQuery ?? ''}
            onQueryChange={onSearchQueryChange}
            results={searchResults ?? []}
            loading={searchLoading ?? false}
            onSelectEvent={onSearchSelect}
            onDeleteEvents={onSearchDeleteEvents}
            onClear={onSearchClear}
            timeFormat={timeFormat}
          />
        )}
      </div>

      {/* Right: View tabs + actions */}
      <div className="flex items-center gap-2">
        <Tabs value={view} onValueChange={(v) => onViewChange(v as CalendarView)}>
          <TabsList className="h-8">
            <TabsTrigger value="month" className="text-xs px-3 h-7">
              Month
            </TabsTrigger>
            <TabsTrigger value="week" className="text-xs px-3 h-7">
              Week
            </TabsTrigger>
            <TabsTrigger value="day" className="text-xs px-3 h-7">
              Day
            </TabsTrigger>
            <TabsTrigger value="agenda" className="text-xs px-3 h-7">
              Agenda
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {onDailyPlanning && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onDailyPlanning}
            title="Plan my day"
          >
            <Sun className="h-4 w-4" />
          </Button>
        )}
        {onEveningReview && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onEveningReview}
            title="Evening review"
          >
            <Sunset className="h-4 w-4" />
          </Button>
        )}
        {onFocusTime && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onFocusTime}
            title="Focus Time"
          >
            <Target className="h-4 w-4" />
          </Button>
        )}
        {onToggleTasks && (
          <Button
            variant={showTaskPanel ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8"
            onClick={onToggleTasks}
            title="Tasks"
          >
            <ListTodo className="h-4 w-4" />
          </Button>
        )}
        {onSchedulingLinks && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onSchedulingLinks}
            title="Scheduling Links"
          >
            <Link className="h-4 w-4" />
          </Button>
        )}
        {onFindTime && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onFindTime}
            title="Find a Time"
          >
            <Users className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => navigate('/a/arche_calendar/analytics')}
          title="Analytics"
        >
          <BarChart3 className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onImport} title="Import">
          <Upload className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onSettings}
          title="Settings"
        >
          <Settings className="h-4 w-4" />
        </Button>
        <Button size="sm" onClick={onNewEvent}>
          <Plus className="h-4 w-4 mr-1" />
          New Event
        </Button>
      </div>
    </div>
  );
}
